import { PageController } from '@client/components/_core/layout/PageController';
import { type TabItem, Tabs } from '@client/components/_core/navigation/Tabs';
import { Button } from '@client/components/_ui/data-entry/ButtonModern';
import { SegmentedControl } from '@client/components/_ui/data-entry/SegmentedControl';
import { UpgradeTag } from '@client/components/_ui/display/UpgradeTag';
import { Loading } from '@client/components/_ui/feedback/Loading';
import { Space } from '@client/components/_ui/layout/Space';
import { useIsTrainingManagerOnly } from '@client/hooks/auth/useIsTrainingManagerOnly';
import { useTrackEngagementView } from '@client/hooks/business/engagement/useTrackEngagementView';
import { useIsLiteOrg } from '@client/hooks/business/org/useIsLiteOrg';
import { useOrgId } from '@client/hooks/business/org/useOrgId';
import { useUser } from '@client/hooks/business/user/useUser';
import { useNavigate } from '@client/hooks/navigation/useNavigate';
import { useAreFeatureFlagsSettled, useFeatureFlag } from '@client/hooks/useFeatureFlag';
import { normalizeRoutePath } from '@client/utils/common/navigation';
import { showLiteUpgradePopover } from '@client/utils/domain/lite';
import { type DashboardSection, isLiteLockedDashboardSection } from '@shared/constants/business/lite';
import { isTruthy } from '@shared/utils/core/boolean';
import { createFileRoute, Outlet, useChildMatches } from '@tanstack/react-router';
import { Activity } from 'lucide-react';
import { type KeyboardEvent, type MouseEvent, useEffect, useRef, useState } from 'react';
import { DashboardContext, useDashboardsQuery } from './dashboard-context';
import {
    DASHBOARD_SECTION_ROUTES,
    getDashboardSectionFeatureFlag,
    isHiddenDashboardSection,
    resolveDashboardSection,
} from './dashboard-routes';
import { useIsCultureDashboardModuleEligible, useIsDashboardsEnabled } from './hooks';
import { INCIDENTS_AND_BREACHES_LABEL, PEOPLE_AND_CULTURE_LABEL } from './sections/dashboard-labels';
import { type ComplaintsDashboardView, useComplaintsDashboardView } from './sections/useComplaintsDashboardView';

export const Route = createFileRoute('/console/$orgId/dashboard')({
    staticData: {
        breadcrumb: {
            title: 'Dashboards',
        },
        roles: ['user', 'shared_edit_user', 'org_admin', 'training_manager'],
        modules: ['claim', 'compliance'],
        ui: {
            widthVariant: 'default',
            icon: Activity,
            title: 'Dashboards',
            subtitle: 'Track performance and compliance metrics across your organisation',
            useTitleForChildren: true,
        },
    },
    component: RouteComponent,
});

function RouteComponent() {
    return (
        <PageController route={Route}>
            <Dashboard />
        </PageController>
    );
}

function Dashboard() {
    const orgId = useOrgId();
    const navigate = useNavigate();
    const childMatches = useChildMatches();
    // The active section is whichever literal child tab route is matched (undefined on the
    // bare /dashboard index, which immediately redirects to the first usable section).
    // Matching is done in route-path space (same space as the `to` literals) rather than
    // via routeId, so a future pathless-layout wrap fails loudly instead of silently.
    const section = resolveDashboardSection(childMatches.map((match) => normalizeRoutePath(match.fullPath)));

    const isDashboardsEnabled = useIsDashboardsEnabled();
    const user = useUser({ throwOnUndefined: false });
    // Keyed to the signed-in user and organisation (as in RouteAccessGuard): a person/group-
    // targeted section flag must be judged against this identity's evaluation, not the
    // anonymous or previous-organisation one that `initialisation: 'ready'` alone reflects.
    const areFlagsSettled = useAreFeatureFlagsSettled({ userId: user?.id.toString(), orgId: orgId.toString() });
    const isCultureModuleEligible = useIsCultureDashboardModuleEligible();

    // A deep link to a flag-gated section is indeterminate until PostHog delivers flags:
    // `useFeatureFlag` reads `false` for both "off" and "still loading", so redirecting on
    // the current value would bounce an authorised user off a cold deep link. Render nothing
    // until the flag evaluation is definitive — but only when the section's non-flag
    // prerequisites hold: a missing compliance module is already definitive, so those deep
    // links bounce immediately without waiting for flags.
    const isSectionPending =
        section !== undefined &&
        !areFlagsSettled &&
        isCultureModuleEligible &&
        getDashboardSectionFeatureFlag(section) !== undefined;

    // A deep link to a section whose tab is hidden for this user (role/module/permission/flag)
    // must never render: antd Tabs falls back to the first visible tab when activeKey matches
    // no item, which would mount the hidden section's content through that tab's Outlet.
    // Bounce to the index, which picks the first usable section. Lite-locked sections never
    // get here — LiteRouteGuard intercepts their paths before this layout renders.
    const isSectionHidden = !isSectionPending && isHiddenDashboardSection(section, isDashboardsEnabled);
    const navigateRef = useRef(navigate);
    navigateRef.current = navigate;
    useEffect(() => {
        if (isSectionHidden) {
            navigateRef.current({ to: '/console/$orgId/dashboard', params: { orgId }, replace: true });
        }
    }, [isSectionHidden, orgId]);

    useTrackEngagementView(
        'dashboard_viewed',
        { section: section ?? '' },
        !!section && !isSectionPending && !isSectionHidden,
    );
    const complaintsV2Enabled = useFeatureFlag('enable-complaints-dashboard-v2', false);
    const showFleetClaimsDashboard = useFeatureFlag('show-fleet-claims-dashboard', false);
    const isLiteOrg = useIsLiteOrg();
    const isTrainingManagerOnly = useIsTrainingManagerOnly();

    // Curium Lite (CUR-379): locked dashboards stay visible as crowned tabs (even where the
    // capped module set would hide them) and clicking opens the contact page instead of
    // navigating. TM-only users keep their minimal view — no locked marketing tabs.
    const showLockedTabs = isLiteOrg && !isTrainingManagerOnly;
    const isSectionLocked = (key: DashboardSection) => isLiteOrg && isLiteLockedDashboardSection(key);
    const lockedLabel = (label: string) => (
        <span className="inline-flex items-center">
            {label}
            <UpgradeTag className="mr-0 ml-2" />
        </span>
    );
    // Curium Lite (CUR-379): crowned tabs open the upgrade popover instead of navigating.
    const lockedNavigate = (_options: { replace?: boolean }, e?: MouseEvent | KeyboardEvent) => {
        showLiteUpgradePopover('dashboard-locked-tab', e?.currentTarget instanceof Element ? e.currentTarget : null);
    };
    const lockableTab = (key: DashboardSection, label: string): TabItem =>
        isSectionLocked(key)
            ? { label: lockedLabel(label), to: DASHBOARD_SECTION_ROUTES[key], onNavigate: lockedNavigate }
            : { label, to: DASHBOARD_SECTION_ROUTES[key] };

    // When v2 is rolled out (flag on), users pick which complaints dashboard to
    // see; defaults to v2 and persists per user. With the flag off there is no
    // toggle and the legacy view is always shown.
    const { view: complaintsView, setView: setComplaintsView } = useComplaintsDashboardView();
    const showComplaintsV2 = complaintsV2Enabled && complaintsView === 'v2';

    // refetchSignal is used only for dashboards that have tiles that fetch data innerly
    const [refetchSignal, setRefetchSignal] = useState({
        complaints: 0,
        incidents: 0,
        peopleAndCulture: 0,
        claims: 0,
    });

    const dashboards = useDashboardsQuery({
        skip: {
            // The old complaints query still feeds the Management overview, so only skip it on the
            // Complaints tab when the V2 view (which fetches its own data) is being shown.
            complaintsDashboard:
                !isDashboardsEnabled.complaints ||
                (section !== 'management' && (section !== 'complaints' || showComplaintsV2)),
            incidentsDashboard: !isDashboardsEnabled.incidents || (section !== 'management' && section !== 'incidents'),
            // People & Culture is premium-locked for Lite (CUR-418) and its data boundary is
            // `liteBlocked` server-side — never fetch it for a locked section, even on direct
            // URL entry to /people-and-culture or via the Management overview.
            peopleAndCultureDashboard:
                !isDashboardsEnabled['people-and-culture'] ||
                isSectionLocked('people-and-culture') ||
                (section !== 'management' && section !== 'people-and-culture'),
            risksDashboard: !isDashboardsEnabled.risks || (section !== 'management' && section !== 'risks'),
        },
    });
    const { loading, refetch } = dashboards;

    const tabs: TabItem[] = [
        (showLockedTabs || isDashboardsEnabled.management) && lockableTab('management', 'Management overview'),
        isDashboardsEnabled.complaints && { label: 'Complaints', to: DASHBOARD_SECTION_ROUTES.complaints },
        isDashboardsEnabled.incidents && {
            label: INCIDENTS_AND_BREACHES_LABEL,
            to: DASHBOARD_SECTION_ROUTES.incidents,
        },
        (showLockedTabs || isDashboardsEnabled['people-and-culture']) &&
            lockableTab('people-and-culture', PEOPLE_AND_CULTURE_LABEL),
        (showLockedTabs || isDashboardsEnabled.risks) && lockableTab('risks', 'Risks'),
        (showLockedTabs || isDashboardsEnabled.claims) && lockableTab('claims', 'Claims'),
    ].filter(isTruthy);

    const setRefetchSignalHelper = (dashboard: keyof typeof refetchSignal) => {
        setRefetchSignal((prev) => ({
            ...prev,
            [dashboard]: prev[dashboard] + 1,
        }));
    };

    const onClick = () => {
        if (isDashboardsEnabled.management && section === 'management') {
            if (isDashboardsEnabled.complaints) {
                void refetch.complaintsDashboardRefetch();
            }

            if (isDashboardsEnabled.incidents) {
                void refetch.incidentsDashboardRefetch();
            }

            if (isDashboardsEnabled['people-and-culture']) {
                refetch.peopleAndCultureDashboard();
            }

            if (isDashboardsEnabled.risks) {
                refetch.risksDashboard();
            }
        }

        if (isDashboardsEnabled.complaints && section === 'complaints') {
            if (showComplaintsV2) {
                void refetch.complaintsV2Dashboard();
            } else {
                setRefetchSignalHelper('complaints');
                void refetch.complaintsDashboardRefetch();
            }
        }

        if (isDashboardsEnabled.incidents && section === 'incidents') {
            setRefetchSignalHelper('incidents');
            void refetch.incidentsDashboardRefetch();
        }

        if (
            isDashboardsEnabled['people-and-culture'] &&
            section === 'people-and-culture' &&
            !isSectionLocked('people-and-culture')
        ) {
            setRefetchSignalHelper('peopleAndCulture');
            refetch.peopleAndCultureDashboard();
        }

        if (isDashboardsEnabled.risks && section === 'risks') {
            void refetch.risksDashboard();
        }

        if (isDashboardsEnabled.claims && section === 'claims') {
            setRefetchSignalHelper('claims');
        }
    };

    if (isSectionPending) {
        return <Loading />;
    }

    // The hidden-section bounce above is immediate. Render nothing while it is in flight so
    // the hidden section's content cannot flash through the first visible tab.
    if (isSectionHidden) {
        return null;
    }

    // On the bare /dashboard index no tab route is matched, and the canonical Tabs only
    // renders its Outlet inside an active tab panel — render a plain Outlet instead so the
    // index route can mount and redirect to the first usable section.
    if (!section) {
        return <Outlet />;
    }

    return (
        <DashboardContext.Provider value={{ dashboards, refetchSignal, showComplaintsV2, showFleetClaimsDashboard }}>
            <Tabs
                items={tabs}
                rightAddon={
                    <Space>
                        {section === 'complaints' && complaintsV2Enabled && (
                            <SegmentedControl<ComplaintsDashboardView>
                                value={complaintsView}
                                onChange={setComplaintsView}
                                variant="quiet"
                                aria-label="Switch complaints dashboard view"
                                options={[
                                    { value: 'v2', label: 'New', tooltip: 'New complaints dashboard' },
                                    { value: 'legacy', label: 'Legacy', tooltip: 'Legacy complaints dashboard' },
                                ]}
                            />
                        )}
                        <Button type="secondary" onClick={onClick} loading={Object.values(loading).includes(true)}>
                            Refresh dashboard
                        </Button>
                    </Space>
                }
            />
        </DashboardContext.Provider>
    );
}
